import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import { pedirAcesso } from "../../../../services/paineisApi";
import { MOTIVO_MAX, MOTIVO_MIN, motivoValido } from "../logica";

interface Props {
  /** Sem `reportId` o pedido é genérico ("quero acesso a um painel"). */
  reportId?: number;
  onClose: () => void;
}

/** Mensagem do servidor (ex.: 429 "limite de pedidos") ou um texto que diz o que fazer. */
function mensagemDoErro(e: unknown): string {
  const r = (e as { response?: { status?: number; data?: { message?: string } } })?.response;
  if (r?.status === 429) return r.data?.message ?? "Você já fez pedidos demais hoje. Tente amanhã.";
  if (r?.status === 404) return "Este painel não está mais disponível para pedido. Atualize a página.";
  return "Não foi possível enviar o pedido. Tente de novo; se continuar, avise o administrador.";
}

const PedirAcessoModal: React.FC<Props> = ({ reportId, onClose }) => {
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!motivoValido(motivo) || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await pedirAcesso({ reportId, motivo: motivo.trim() });
      toast.success("Pedido enviado. O administrador recebe um chamado.");
      onClose();
    } catch (e) {
      setErro(mensagemDoErro(e));
      setEnviando(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void enviar(e)}
        className="w-full max-w-md rounded-[20px] bg-white p-6 shadow-dv-md dark:bg-navy-800"
      >
        <h2 className="font-display text-lg font-bold text-navy-700 dark:text-white">Pedir acesso</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Conte por que você precisa deste acesso. O administrador da sua organização recebe um chamado.
        </p>
        <label htmlFor="motivo-acesso" className="mt-4 block text-sm font-semibold text-navy-700 dark:text-white">
          Motivo
        </label>
        <textarea
          id="motivo-acesso"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={MOTIVO_MAX}
          rows={4}
          className="mt-1 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-navy-700 outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/15 dark:bg-navy-900 dark:text-white dark:focus-visible:ring-dv-cyan-300"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Mínimo de {MOTIVO_MIN} caracteres ({motivo.trim().length}/{MOTIVO_MAX}).
        </p>
        {erro && (
          <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">
            {erro}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:text-gray-200 dark:hover:bg-navy-700 dark:focus-visible:ring-dv-cyan-300"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!motivoValido(motivo) || enviando}
            className="rounded-xl bg-dv-blue-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 focus-visible:ring-offset-2 dark:bg-dv-cyan-300 dark:text-navy-900 dark:focus-visible:ring-dv-cyan-300"
          >
            {enviando ? "Enviando..." : "Enviar pedido"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PedirAcessoModal;
